import { inject, Injectable } from '@angular/core';
import { InternationalizationEnvironment } from 'app/core/configuration';
import { catchError, EMPTY, filter, Observable, switchMap, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateManagerService } from '../shared/state/state-manager.service';
import { LangMap } from '../views/auth/model/languages-config';
import { AnalyticsDataService } from './external/analytics/analytics.service';
import { OidcLoginResponse } from './external/auth-oidc/models/authOidc.interface';
import { SessionOidcService } from './external/auth-oidc/session-oidc.service';
import { OneTrustService } from './external/cookies/one-trust.service';
import { MonetateService } from './external/monetate/monetate-service';
import { CurrencyService } from './internal/currency/currency.service';
import { LanguageService } from './internal/language/language.service';

@Injectable({ providedIn: 'root' })
export class AppInitializerService {
  private readonly _sessionOidcService = inject(SessionOidcService);
  private readonly _stateManagerService = inject(StateManagerService);
  private readonly _languageService = inject(LanguageService);
  private readonly _analyticsService = inject(AnalyticsDataService);
  private readonly _oneTrustService = inject(OneTrustService);
  private readonly _monetateService = inject(MonetateService);
  private readonly _currencyService = inject(CurrencyService);

  public initializeApp(): Observable<void> {
    this.updatedLangBrowser(environment.internationalization);
    this._oneTrustService.handleCookieConsent();
    return this._sessionOidcService.checkCurrentSession().pipe(
      take(1),
      switchMap((auth: OidcLoginResponse) => {
        if (!auth.isAuthenticated) {
          return this.executeLoginOidc();
        }
        this._monetateService.initialize();
        this._stateManagerService.initialize();
        this._analyticsService.startGoogleAnalytics();
        const cultureCode = this._sessionOidcService.currentSessionOidc().userData?.cultureCode;
        this._languageService.initialize(environment.internationalization, this.checkLanguage(cultureCode));
        this._currencyService.initialize(environment.internationalization.defaaultCurrency);
        return EMPTY;
      }),
      catchError(() => {
        return this.executeLoginOidc();
      })
    );
  }

  private checkLanguage(lan: string): string {
    return LangMap[lan] ?? environment.internationalization.defaultLanguage;
  }

  private executeLoginOidc(): Observable<void> {
    this._oneTrustService
      .cookieConsentGiven()
      .pipe(
        take(1),
        filter((consent) => !!consent)
      )
      .subscribe(() => {
        this._sessionOidcService.authorize();
      });
    return EMPTY;
  }

  private updatedLangBrowser(config: InternationalizationEnvironment): void {
    const checkedLang = config.languages.find((lang) => lang.startsWith(navigator.language?.split('-')[0]));
    this._languageService.forceTemporaryLanguage(checkedLang ?? config.defaultLanguage);
  }
}
